/**
 * Device Detection Utility
 *
 * Detects device type, OS, browser, and model from user agent.
 * Uses pure user agent parsing without external dependencies.
 */

export type DeviceType = 'desktop' | 'tablet' | 'mobile' | 'tv' | 'other'
export type OS = 'Windows' | 'macOS' | 'iOS' | 'Android' | 'Linux' | 'Chrome OS' | 'Windows Phone' | 'unknown'
export type Browser = 'Chrome' | 'Safari' | 'Firefox' | 'Edge' | 'Opera' | 'Samsung' | 'IE' | 'unknown'

export interface DeviceInfo {
    deviceType: DeviceType
    os: OS
    osVersion: string
    browser: Browser
    browserVersion: string
    model: string
    isMobile: boolean
    isTablet: boolean
    isDesktop: boolean
    screenWidth: number
    screenHeight: number
    viewportWidth: number
    viewportHeight: number
    pixelRatio: number
    touchSupport: boolean
    language: string
    timezone: string
}

/**
 * Detect the device type (desktop, tablet, mobile, tv, other)
 */
export function detectDeviceType(): DeviceType {
    if (typeof window === 'undefined') return 'desktop'
    
    const ua = navigator.userAgent.toLowerCase()
    
    // Smart TV detection
    if (/smart-tv|smarttv|googletv|appletv|hbbtv|pov_tv|netcast/i.test(ua)) {
        return 'tv'
    }
    
    // Gaming consoles (treated as other)
    if (/xbox|playstation|nintendo|wii/i.test(ua)) {
        return 'other'
    }
    
    // Tablet detection (must come before mobile)
    if (/ipad/i.test(ua)) {
        return 'tablet'
    }
    
    // Android tablet detection
    if (/android/i.test(ua) && !/mobile/i.test(ua)) {
        return 'tablet'
    }
    
    // Kindle Fire
    if (/kindle|silk/i.test(ua)) {
        return 'tablet'
    }
    
    // Mobile detection
    if (/mobile|iphone|ipod|blackberry|opera mini|opera mobi/i.test(ua)) {
        return 'mobile'
    }
    
    // Android mobile
    if (/android/i.test(ua) && /mobile/i.test(ua)) {
        return 'mobile'
    }
    
    // Default to desktop
    return 'desktop'
}

/**
 * Detect the operating system
 */
export function detectOS(): { os: OS; version: string } {
    if (typeof window === 'undefined') return { os: 'unknown', version: '' }
    
    const ua = navigator.userAgent
    
    // iOS detection (must come before macOS)
    if (/iPad|iPhone|iPod/.test(ua)) {
        const match = ua.match(/OS (\d+)[._]?(\d+)?[._]?(\d+)?/)
        const version = match ? `${match[1]}.${match[2] || 0}` : ''
        return { os: 'iOS', version }
    }
    
    // Android
    if (/Android/.test(ua)) {
        const match = ua.match(/Android (\d+)[._]?(\d+)?[._]?(\d+)?/)
        const version = match ? `${match[1]}.${match[2] || 0}` : ''
        return { os: 'Android', version }
    }
    
    // Windows Phone
    if (/Windows Phone/.test(ua)) {
        const match = ua.match(/Windows Phone (\d+)[._]?(\d+)?/)
        const version = match ? `${match[1]}.${match[2] || 0}` : ''
        return { os: 'Windows Phone', version }
    }
    
    // Windows
    if (/Windows NT/.test(ua)) {
        const match = ua.match(/Windows NT (\d+)[._]?(\d+)?/)
        const version = match ? `${match[1]}.${match[2] || 0}` : ''
        return { os: 'Windows', version }
    }
    
    // macOS
    if (/Mac OS X/.test(ua)) {
        const match = ua.match(/Mac OS X (\d+)[._]?(\d+)?[._]?(\d+)?/)
        const version = match ? `${match[1]}.${match[2] || 0}` : ''
        return { os: 'macOS', version }
    }
    
    // Chrome OS
    if (/CrOS/.test(ua)) {
        const match = ua.match(/CrOS [a-z0-9_]+ (\d+)[._]?(\d+)?/)
        const version = match ? `${match[1]}.${match[2] || 0}` : ''
        return { os: 'Chrome OS', version }
    }
    
    // Linux
    if (/Linux/.test(ua)) {
        return { os: 'Linux', version: '' }
    }
    
    return { os: 'unknown', version: '' }
}

/**
 * Detect the browser
 */
export function detectBrowser(): { browser: Browser; version: string } {
    if (typeof window === 'undefined') return { browser: 'unknown', version: '' }
    
    const ua = navigator.userAgent
    
    // Edge (Chromium-based) - must come before Chrome
    if (/Edg\//.test(ua)) {
        const match = ua.match(/Edg\/(\d+)[._]?(\d+)?/)
        const version = match ? `${match[1]}.${match[2] || 0}` : ''
        return { browser: 'Edge', version }
    }
    
    // Opera - must come before Chrome
    if (/OPR\//.test(ua) || /Opera/.test(ua)) {
        const match = ua.match(/(?:OPR|Opera)\/(\d+)[._]?(\d+)?/)
        const version = match ? `${match[1]}.${match[2] || 0}` : ''
        return { browser: 'Opera', version }
    }
    
    // Samsung Internet - must come before Chrome
    if (/SamsungBrowser\//.test(ua)) {
        const match = ua.match(/SamsungBrowser\/(\d+)[._]?(\d+)?/)
        const version = match ? `${match[1]}.${match[2] || 0}` : ''
        return { browser: 'Samsung', version }
    }
    
    // Firefox
    if (/Firefox\//.test(ua)) {
        const match = ua.match(/Firefox\/(\d+)[._]?(\d+)?/)
        const version = match ? `${match[1]}.${match[2] || 0}` : ''
        return { browser: 'Firefox', version }
    }
    
    // Internet Explorer
    if (/MSIE|Trident/.test(ua)) {
        const match = ua.match(/(?:MSIE |rv:)(\d+)[._]?(\d+)?/)
        const version = match ? `${match[1]}.${match[2] || 0}` : ''
        return { browser: 'IE', version }
    }
    
    // Chrome
    if (/Chrome\//.test(ua)) {
        const match = ua.match(/Chrome\/(\d+)[._]?(\d+)?/)
        const version = match ? `${match[1]}.${match[2] || 0}` : ''
        return { browser: 'Chrome', version }
    }
    
    // Safari (must come last as many browsers include Safari in UA)
    if (/Safari\//.test(ua)) {
        const match = ua.match(/Version\/(\d+)[._]?(\d+)?/)
        const version = match ? `${match[1]}.${match[2] || 0}` : ''
        return { browser: 'Safari', version }
    }
    
    return { browser: 'unknown', version: '' }
}

/**
 * Detect device model (mainly for mobile devices)
 */
export function detectModel(): string {
    if (typeof window === 'undefined') return ''
    
    const ua = navigator.userAgent
    
    // iPhone
    const iPhoneMatch = ua.match(/iPhone([0-9]+,[0-9]+)/i)
    if (iPhoneMatch) {
        // Map iPhone model numbers to readable names
        const modelMap: Record<string, string> = {
            '12,1': 'iPhone 11',
            '12,3': 'iPhone 11 Pro',
            '12,5': 'iPhone 11 Pro Max',
            '13,1': 'iPhone 12 mini',
            '13,2': 'iPhone 12',
            '13,3': 'iPhone 12 Pro',
            '13,4': 'iPhone 12 Pro Max',
            '14,2': 'iPhone 13 Pro',
            '14,3': 'iPhone 13 Pro Max',
            '14,4': 'iPhone 13 mini',
            '14,5': 'iPhone 13',
            '15,2': 'iPhone 14 Pro',
            '15,3': 'iPhone 14 Pro Max',
            '14,7': 'iPhone 14',
            '14,8': 'iPhone 14 Plus',
            '16,1': 'iPhone 15 Pro',
            '16,2': 'iPhone 15 Pro Max',
            '16,3': 'iPhone 15',
            '15,4': 'iPhone 15 Plus',
        }
        return modelMap[iPhoneMatch[1]] || `iPhone (${iPhoneMatch[1]})`
    }
    
    // Generic iPhone
    if (/iPhone/.test(ua)) return 'iPhone'
    
    // iPad
    if (/iPad/.test(ua)) {
        const iPadMatch = ua.match(/iPad([0-9]+,[0-9]+)/i)
        if (iPadMatch) return `iPad (${iPadMatch[1]})`
        return 'iPad'
    }
    
    // Android device detection
    const androidMatch = ua.match(/Android.*?;\s*([^;)]+)\s*(?:Build|;|\))/i)
    if (androidMatch) {
        let model = androidMatch[1].trim()
        // Clean up common patterns
        model = model.replace(/^(SM\-|GT\-|Nexus|Pixel)/, (m) => {
            if (m === 'Nexus' || m === 'Pixel') return m + ' '
            return '' // Samsung model codes are fine as-is
        })
        return model
    }
    
    return ''
}

/**
 * Get comprehensive device information
 */
export function getDeviceInfo(): DeviceInfo {
    if (typeof window === 'undefined') {
        return {
            deviceType: 'desktop',
            os: 'unknown',
            osVersion: '',
            browser: 'unknown',
            browserVersion: '',
            model: '',
            isMobile: false,
            isTablet: false,
            isDesktop: true,
            screenWidth: 0,
            screenHeight: 0,
            viewportWidth: 0,
            viewportHeight: 0,
            pixelRatio: 1,
            touchSupport: false,
            language: '',
            timezone: '',
        }
    }
    
    const deviceType = detectDeviceType()
    const { os, version: osVersion } = detectOS()
    const { browser, version: browserVersion } = detectBrowser()
    const model = detectModel()
    
    return {
        deviceType,
        os,
        osVersion,
        browser,
        browserVersion,
        model,
        isMobile: deviceType === 'mobile',
        isTablet: deviceType === 'tablet',
        isDesktop: deviceType === 'desktop',
        screenWidth: screen.width,
        screenHeight: screen.height,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        pixelRatio: window.devicePixelRatio || 1,
        touchSupport: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
        language: navigator.language || (navigator as any).userLanguage || '',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    }
}

/**
 * Get a compact device summary for analytics
 */
export function getDeviceSummary(): {
    device_type: string
    os: string
    os_version: string
    browser: string
    browser_version: string
    model: string
    screen_width: number
    screen_height: number
    viewport_width: number
    viewport_height: number
    pixel_ratio: number
    touch_support: boolean
    language: string
    timezone: string
} {
    const info = getDeviceInfo()
    
    return {
        device_type: info.deviceType,
        os: info.os,
        os_version: info.osVersion,
        browser: info.browser,
        browser_version: info.browserVersion,
        model: info.model,
        screen_width: info.screenWidth,
        screen_height: info.screenHeight,
        viewport_width: info.viewportWidth,
        viewport_height: info.viewportHeight,
        pixel_ratio: info.pixelRatio,
        touch_support: info.touchSupport,
        language: info.language,
        timezone: info.timezone,
    }
}

export default getDeviceInfo
